import { FC } from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import {
  useAddBusinessPlan,
  useWorkspacesFeatureEnabled,
} from '@editor/global/growthbookUtils';
import { Badge } from '@repo/ui/components/Badge';
import { Button, primaryTheme } from '@repo/ui/components/Buttons';
import CrownIcon from '@repo/ui/components/Icon/CrownIcon';
import CheckIcon from '@repo/ui/components/Icon/v2/CheckIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H3, P2 } from '@repo/ui/components/Typography';
import { themeStyle } from '@repo/ui/theming';

import {
  MonetaryValue,
  PricingPlanAddon,
  PricingPlanPrice,
} from '../../types/Pricing';
import { formatMonetaryValue, getYearlyCharge } from '../../utils/billing';
import { Divider } from '../utilities/styles';

import InfoIconModalPopover from './InfoIconModalPopover';
import {
  BadgeContainer,
  PriceLabel,
  Feature,
  FeatureLabel,
  ContentPlanBadge,
  Features,
  PriceCustomLabel,
} from './styles';
import { PricingPlanProps } from './types';
import { getPlanCostsPerMonth } from './utils';

/**
 * Generates a label for the yearly billing amount of a pricing plan.
 *
 * @param {PricingPlanPrice['yearly']} planPrice - The yearly price of the plan, including the amount and currency.
 * @param {PricingPlanAddon} [addon] - An optional addon that may increase the total amount.
 * @param {number} [quantity=1] - The quantity of the plan being purchased, defaults to 1.
 * @returns {string} - A formatted string indicating the total amount billed annually.
 *
 * The function calculates the total yearly amount by multiplying the base amount by the quantity
 * and adding any addon amount if provided. It then formats the total amount using the appropriate
 * number of fraction digits (2 for Indonesian Rupiah, 0 for other currencies) and returns a string
 * indicating the total amount billed annually.
 */
const getYearlyLabel = (
  planPrice: PricingPlanPrice['yearly'],
  addon?: PricingPlanAddon,
  quantity = 1,
  businessPlanIsEnabled = false,
  isBusinessPlan = false
): string => {
  const { amount, currency } = planPrice;
  const price: MonetaryValue = {
    currency,
    amount: amount * quantity + (addon?.amount ?? 0),
  };

  if (businessPlanIsEnabled) {
    return `Billed annually${isBusinessPlan ? ', min. 3 Editors' : ''}`;
  }
  // The localized price for Indonesia is a large number which is shortened by default, so we need to format it with 2 fraction digits. Such that 3740000 will be 3.75M and not 4M.
  // Other currencies are to be whole numbers as in $288, €120, £200
  const formattedPrice = formatMonetaryValue(getYearlyCharge(price), {
    fractionDigits: currency === 'IDR' ? 2 : 0,
  });

  return `Billed ${formattedPrice} annually`;
};

/**
 * PlanDetails component
 *
 * This component displays the details of a pricing plan, including the plan name, description, monthly and yearly costs,
 * and the label for the yearly billing amount. It also renders the features of the selected plan.
 */
export const PlanDetails: FC<PricingPlanProps> = (props) => {
  const isEnabled = useWorkspacesFeatureEnabled();
  const businessPlanIsEnabled = useAddBusinessPlan();

  const { yearly: yearlyPricePerMonth, monthly: monthlyPricePerMonth } =
    props.price
      ? getPlanCostsPerMonth({
          planPricing: props.price,
          yearly: props.yearly,
          quantity: props.quantity,
          selectedAddon: props.selectedAddon,
        })
      : { yearly: null, monthly: null };

  const priceLabel =
    props.price?.label ??
    (props.yearly && props.price
      ? getYearlyLabel(
          props.price.yearly,
          props.selectedAddon,
          props.quantity,
          businessPlanIsEnabled,
          props.type === 'BUSINESS'
        )
      : '');

  // Show the full price with a strikethrough
  const UndiscountedPrice: React.FC<{ price: string }> = ({
    price,
  }: {
    price: string;
  }) => {
    return (
      <div
        style={{
          marginLeft: '8px',
        }}
      >
        <Badge textStrikeThrough size="m" text={price} variant="blue" />
      </div>
    );
  };

  return (
    <div>
      {props.isContentPlan && <ContentPlanBadge />}

      {/* Renders plan name, description, and price */}
      <FlexColumn width="100%">
        <FlexRow style={{ gap: '8px', width: '100%', alignItems: 'center' }}>
          <H3>
            <b>{props.title}</b>
          </H3>
          {props.label && (
            <BadgeContainer
              style={{
                background: `${themeStyle.highlight}`,
                color: `${themeStyle.inkBase}`,
                fontWeight: `${themeStyle.fontBold}`,
                borderRadius: '360px',
              }}
            >
              {props.label}
            </BadgeContainer>
          )}
        </FlexRow>
        {!props.hideDescription && (
          <>
            <Spacer height="12px" />
            {typeof props.description === 'string' ? (
              <P2
                color={themeStyle.varInkMain}
                dangerouslySetInnerHTML={{ __html: props.description }}
              />
            ) : (
              <P2 color={themeStyle.varInkMain}>{props.description}</P2>
            )}
          </>
        )}
        <Spacer height="24px" />
        {!props.hidePrice && monthlyPricePerMonth && yearlyPricePerMonth && (
          <>
            {props.type ? (
              <PriceLabel>
                <H3 style={{ fontWeight: `${themeStyle.fontBold}` }}>
                  {formatMonetaryValue(
                    props.yearly ? yearlyPricePerMonth : monthlyPricePerMonth,
                    {
                      fractionDigits: 0,
                    }
                  )}
                </H3>
                <FlexRow alignItems="center">
                  <P2>
                    / month{' '}
                    {isEnabled && props.type !== 'FREE' ? 'per editor' : ''}
                  </P2>
                  {props.yearly && yearlyPricePerMonth.amount !== 0 && (
                    <UndiscountedPrice
                      price={formatMonetaryValue(monthlyPricePerMonth, {
                        fractionDigits: 0,
                      })}
                    />
                  )}
                </FlexRow>
              </PriceLabel>
            ) : (
              <PriceCustomLabel>Custom</PriceCustomLabel>
            )}
            <Spacer height="4px" />
            <P2 color={themeStyle.varInkMedium}>{priceLabel}</P2>
          </>
        )}

        {/* Member select for teams */}
        {props.memberSelect && (
          <>
            <Spacer height="14px" />
            {props.memberSelect}
          </>
        )}
        {props.button && (
          <>
            <Spacer height="24px" />
            <Button
              name="planDetailsActionButton"
              {...props.button}
              disabled={props.button?.disabled}
              height="40px"
              label={props.button?.label ?? props.buttonLabel}
              theme={props.button?.theme ?? primaryTheme}
              width="100%"
              onClick={props.button.onClick}
            />
            <Spacer height="24px" />
          </>
        )}
      </FlexColumn>

      {/* Renders the features of the selected plan */}
      <FlexColumn gap="12px" width="100%">
        <FeatureLabel>{props.featuresLabel}</FeatureLabel>
        <Features>
          <ul>
            {/* Main features */}
            {props.mainFeatures.map((feature, index) => {
              return (
                <Feature key={index}>
                  <CheckIcon
                    height="16px"
                    theme={{
                      color: themeStyle.varBlueMedium,
                    }}
                  />
                  <div className="content">
                    <span className="label">
                      {feature.bold ? <b>{feature.label}</b> : feature.label}
                      {feature.premium && (
                        <i className="icon">
                          <CrownIcon height="10px" />
                        </i>
                      )}
                      {feature.infoIconModalContent && (
                        <InfoIconModalPopover
                          infoModalData={feature.infoIconModalContent}
                        />
                      )}
                    </span>
                    <span className="details">{feature.details}</span>
                  </div>
                </Feature>
              );
            })}
            {props.hideSeparator ? null : (
              <Divider color={themeStyle.varInkLight} />
            )}
            {/* Other features */}
            {props.otherFeatures &&
              props.otherFeatures
                .slice(
                  0,
                  props.otherFeaturesCount || props.otherFeatures.length
                )
                .map((feature, index) => {
                  return (
                    <Feature key={index}>
                      <CheckIcon
                        height="16px"
                        theme={{
                          color: themeStyle.varBlueMedium,
                        }}
                      />
                      <div className="content">
                        <span className="label">
                          {feature.bold ? (
                            <b>{feature.label}</b>
                          ) : (
                            feature.label
                          )}
                          {feature.premium && (
                            <i className="icon">
                              <CrownIcon height="10px" />
                            </i>
                          )}
                          {feature.infoIconModalContent && (
                            <InfoIconModalPopover
                              infoModalData={feature.infoIconModalContent}
                            />
                          )}
                        </span>
                        <span className="details">{feature.details}</span>
                      </div>
                    </Feature>
                  );
                })}
          </ul>
        </Features>
      </FlexColumn>
    </div>
  );
};
